/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useMemo } from 'react';
import Highcharts from 'highcharts/es-modules/masters/highcharts.src';
import HighchartsReact from 'highcharts-react-official';

interface LabourImpactAgeStackedBarProps {
  data: any[];
  sectorCodes: string[];
  sectorDescs: string[];
  iloKeys: string[];
  chartHeight: number;
  formatCellNumber: (val: number, hideK?: boolean) => string;
}

const LabourImpactAgeStackedBar: FC<LabourImpactAgeStackedBarProps> = ({ 
  data, sectorCodes, sectorDescs, iloKeys, chartHeight, formatCellNumber 
}) => {
  const options = useMemo(() => ({
    chart: { 
      type: 'bar', 
      height: chartHeight,
      marginTop: 50,
      marginBottom: 50,
      backgroundColor: 'transparent'
    },
    title: { text: '' },
    xAxis: {
      categories: sectorCodes,
      reversed: false,
      labels: {
        step: 1,
        style: { fontSize: '0.85rem' }
      }
    },
    yAxis: {
      title: { text: 'Workforce Size (Thousands)' },
      labels: {
        style: { fontSize: '0.8rem' }
      }
    },
    legend: {
      enabled: true,
      verticalAlign: 'top',
      itemStyle: { fontWeight: 'normal', fontSize: '12px' }
    },
    credits: { enabled: false },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function (this: any) {
        let s = `<div style="padding: 5px"><b>[${sectorCodes[this.x]}] ${sectorDescs[this.x]}</b><br/>`;
        s += `<table style="width: 100%; border-top: 1px solid #eee; margin-top: 5px">`;
        this.points.forEach((p: any) => {
          const val = formatCellNumber(p.y);
          s += `<tr><td style="color:${p.series.color}">${p.series.name}:</td><td style="text-align:right; padding-left:10px"><b>${val}</b></td></tr>`;
        });
        s += `</table></div>`;
        return s;
      }
    },
    plotOptions: {
      series: {
        stacking: 'normal',
        borderWidth: 0,
        pointPadding: 0,
        groupPadding: 0,
        dataLabels: {
          enabled: true,
          inside: true,
          formatter: function (this: any) {
            if (Math.abs(this.y) < 0.1) return '';
            return formatCellNumber(this.y, true);
          },
          style: { 
            fontWeight: 'normal', 
            fontSize: '11px',
            textOutline: 'none',
            color: 'contrast'
          }
        }
      }
    },
    series: iloKeys.flatMap((key, idx) => {
      const colors = ['#007104', '#004a80', '#ed7d31', '#7030a0', '#666'];
      const baseColor = colors[idx % colors.length];
      return [
        {
          name: `${key} Count`,
          data: data.map(row => parseFloat(row.ilo[key]?.count || 0)),
          color: baseColor,
          stack: 'count'
        },
        {
          name: `${key} Change`,
          data: data.map(row => parseFloat(row.ilo[key]?.change || 0)),
          color: new (Highcharts as any).Color(baseColor).brighten(0.3).get(),
          stack: 'change'
        }
      ];
    })
  }), [data, sectorCodes, chartHeight, iloKeys]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default LabourImpactAgeStackedBar;
